import axios from 'axios';

const AUTH_URL =  import.meta.env.VITE_AUTH_URL;
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';
const AUTH_URL_LOCAL = "https://supporthub-api.elevatebr.org/"
const CLIENT_ID = import.meta.env.VITE_CLIENT_ID || '0c61ff08-282f-4b5a-b46b-1238230a0760';
const CLIENT_SECRET = import.meta.env.VITE_CLIENT_SECRET || '00e79631-5956-4f0a-995c-74230b2cb4b4';
const REDIRECT_URI =
  import.meta.env.VITE_REDIRECT_URI || 'https://supporthub.elevatebr.org/callback';

/** Renovar o access token esta quantidade de ms antes do exp (evita logout na UI antes do axios). */
const PROACTIVE_REFRESH_LEEWAY_MS = 60_000;

/** Disparado no `window` após access (e refresh) salvos — ex.: LoginPage se o refresh vencer a corrida com PrivateRoute. */
export const AUTH_SESSION_TOKENS_UPDATED_EVENT = 'supporthub-auth-tokens-updated';

const notifyAuthTokensUpdated = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_SESSION_TOKENS_UPDATED_EVENT));
  }
};

let proactiveRefreshTimerId = null;
/** Evita vários POST /token em paralelo (interceptor + timer + várias abas parcialmente mitigado). */
let refreshAccessTokenInFlight = null;
/** Impede o refresh de gravar token de novo depois do logout. */
let sessionEnding = false;
/** Logout explícito: não desviar para /login antes de encerrar a sessão do provedor. */
let endingAuthSession = false;

const isLogoutRequest = (config) => String(config?.url || '').includes('/user/logout');
const FORCE_LOGIN_KEY = 'supporthub_force_login';
const SESSION_REVOKED_KEY = 'supporthub_session_revoked';

const markForceLogin = () => {
  try {
    sessionStorage.setItem(FORCE_LOGIN_KEY, '1');
  } catch {
    // sessionStorage indisponível: o próximo login segue sem prompt.
  }
};

const shouldForceLogin = () => {
  try {
    return sessionStorage.getItem(FORCE_LOGIN_KEY) === '1';
  } catch {
    return false;
  }
};

const clearForceLogin = () => {
  try {
    sessionStorage.removeItem(FORCE_LOGIN_KEY);
  } catch {
    // ignore
  }
};

const isSessionRevoked = () => {
  try {
    return localStorage.getItem(SESSION_REVOKED_KEY) === '1';
  } catch {
    return false;
  }
};

const markSessionRevoked = () => {
  try {
    localStorage.setItem(SESSION_REVOKED_KEY, '1');
  } catch {
    // ignore
  }
  markForceLogin();
};

const clearSessionRevoked = () => {
  try {
    localStorage.removeItem(SESSION_REVOKED_KEY);
  } catch {
    // ignore
  }
  clearForceLogin();
};

export const requiresFreshLogin = () => shouldForceLogin() || isSessionRevoked();

export const appendForceLogin = (params) => {
  if (!requiresFreshLogin()) return;
  // Este servidor ignora prompt=login. max_age=0 é o sinal OIDC de reautenticação.
  params.set('prompt', 'login');
  params.set('max_age', '0');
};

const authOrigin = () => String(AUTH_URL || '').replace(/\/oauth2\/?$/, '');

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('timeout')), ms);
    }),
  ]);

const loadAuthMetadata = async () => {
  try {
    const response = await axios.get(`${authOrigin()}/.well-known/openid-configuration`, {
      timeout: 8000,
    });
    return response.data || {};
  } catch (error) {
    console.error('Error loading auth metadata:', error);
    return {};
  }
};

const revokeRefreshToken = async (refreshToken) => {
  const metadata = await loadAuthMetadata();
  const revokeUrl = metadata.revocation_endpoint || `${AUTH_URL}/revoke`;
  const params = new URLSearchParams();
  params.append('token', refreshToken);
  params.append('token_type_hint', 'refresh_token');
  await axios.post(revokeUrl, params.toString(), {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Basic ${btoa(`${CLIENT_ID}:${CLIENT_SECRET}`)}`,
    },
    timeout: 8000,
  });
};

const PENDING_AUTHORIZE_KEY = 'supporthub_pending_authorize';
const IDP_LOGOUT_WAIT_MS = 1500;

const buildEndSessionUrl = () => {
  // O cookie JSESSIONID fica neste host. GET /logout invalida a HttpSession.
  // /connect/logout do issuer não apaga esse cookie, e prompt=login é ignorado.
  return `${authOrigin()}/logout`;
};

/**
 * Mata a sessão do provedor numa navegação de topo (o cookie SameSite=Lax
 * só vai em GET de topo, não em fetch) e só então segue para nextUrl.
 */
export const endIdpSessionThen = (nextUrl) => {
  const logoutUrl = buildEndSessionUrl();
  const popup = window.open(logoutUrl, 'supporthub-idp-logout', 'popup,width=520,height=680');
  if (!popup) {
    try {
      sessionStorage.setItem(PENDING_AUTHORIZE_KEY, nextUrl);
    } catch {
      // sem sessionStorage: o próximo clique em Entrar tenta de novo
    }
    window.location.assign(logoutUrl);
    return;
  }
  window.setTimeout(() => {
    try {
      popup.close();
    } catch {
      // popup já fechado
    }
    window.location.assign(nextUrl);
  }, IDP_LOGOUT_WAIT_MS);
};

export const consumePendingAuthorize = () => {
  try {
    const url = sessionStorage.getItem(PENDING_AUTHORIZE_KEY);
    if (!url) return null;
    sessionStorage.removeItem(PENDING_AUTHORIZE_KEY);
    return url;
  } catch {
    return null;
  }
};

const clearStoredTokens = () => {
  sessionEnding = true;
  clearProactiveRefreshSchedule();
  refreshAccessTokenInFlight = null;
  markSessionRevoked();
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('id_token');
};

const clearProactiveRefreshSchedule = () => {
  if (proactiveRefreshTimerId != null) {
    clearTimeout(proactiveRefreshTimerId);
    proactiveRefreshTimerId = null;
  }
};

/**
 * Ms até disparar o refresh proativo. Nunca retorna valores que causem loop de setTimeout(0)
 * quando o JWT ainda é válido (evita reagendar imediato infinito).
 */
const getProactiveRefreshDelayMs = (expMs) => {
  const now = Date.now();
  const msToExp = expMs - now;
  if (msToExp <= 0) return 0;

  const atLeeway = expMs - PROACTIVE_REFRESH_LEEWAY_MS - now;
  const MIN_SPREAD_MS = 5_000;

  if (atLeeway >= MIN_SPREAD_MS) return atLeeway;
  if (msToExp <= 2_000) return Math.max(0, msToExp - 500);
  return MIN_SPREAD_MS;
};

/**
 * Agenda uma renovação silenciosa antes do JWT expirar.
 */
const scheduleProactiveAccessTokenRefresh = () => {
  clearProactiveRefreshSchedule();

  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');
  if (!accessToken || !refreshToken) return;

  let expMs;
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    if (typeof payload.exp !== 'number') return;
    expMs = payload.exp * 1000;
  } catch {
    return;
  }

  const delay = getProactiveRefreshDelayMs(expMs);

  proactiveRefreshTimerId = window.setTimeout(async () => {
    proactiveRefreshTimerId = null;
    const rt = localStorage.getItem('refresh_token');
    if (!rt) return;
    const at = localStorage.getItem('access_token');
    if (!at) return;
    if (!isTokenExpired(at)) {
      scheduleProactiveAccessTokenRefresh();
      return;
    }
    try {
      await refreshAccessToken(rt);
    } catch (e) {
      console.error('Proactive token refresh failed:', e);
      clearProactiveRefreshSchedule();
      const status = e?.response?.status;
      if (status === 400 || status === 401) {
        logout();
        return;
      }
      proactiveRefreshTimerId = window.setTimeout(() => {
        proactiveRefreshTimerId = null;
        scheduleProactiveAccessTokenRefresh();
      }, 30_000);
    }
  }, delay);
};

// Função auxiliar para verificar se o token está expirado
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

const getTokenPayload = (token) => {
  let t = token;
  if (!t && typeof window !== 'undefined' && window.location.pathname.startsWith('/embed/')) {
    t = sessionStorage.getItem('supporthub_embed_token');
  }
  if (!t) {
    t = localStorage.getItem('access_token');
  }
  if (!t) return null;
  try {
    return JSON.parse(atob(t.split('.')[1]));
  } catch {
    return null;
  }
};

/** Retorna o user_type do JWT (ex.: 'CLIENT', 'ADMIN') ou null. */
export const getUserType = () => {
  const payload = getTokenPayload();
  return payload?.user_type ?? null;
};

/** Retorna o nome completo do usuário do JWT (campo user_fullname) ou fallback. */
export const getUserFullname = () => {
  const payload = getTokenPayload();
  return payload?.user_fullname ?? null;
};

/** Retorna o sub do JWT (identificador do usuário) ou null. */
export const getSub = () => {
  const payload = getTokenPayload();
  return payload?.sub ?? null;
};

/** Retorna a rota padrão conforme user_type: ADMIN -> suporte, CLIENT -> usuário. */
export const getDefaultRedirectPathByUserType = () => {
  const userType = getUserType();
  if (userType === 'ADMIN') return '/all-tickets';
  return '/user/tickets';
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

export const EMBED_TOKEN_STORAGE_KEY = 'supporthub_embed_token';

export const isEmbedMode = () =>
  typeof window !== 'undefined' && window.location.pathname.startsWith('/embed/');

export const setEmbedAccessToken = (token) => {
  if (typeof sessionStorage === 'undefined') return;
  if (token) {
    sessionStorage.setItem(EMBED_TOKEN_STORAGE_KEY, token);
  } else {
    sessionStorage.removeItem(EMBED_TOKEN_STORAGE_KEY);
  }
};

export const getEmbedAccessToken = () => {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem(EMBED_TOKEN_STORAGE_KEY);
};

export const clearEmbedAccessToken = () => {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(EMBED_TOKEN_STORAGE_KEY);
};

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    console.log('Request interceptor - URL:', config.url);

    // FormData precisa do boundary gerado pelo browser (não application/json).
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers) {
        if (typeof config.headers.delete === 'function') {
          config.headers.delete('Content-Type');
          config.headers.delete('content-type');
        } else {
          delete config.headers['Content-Type'];
          delete config.headers['content-type'];
        }
      }
    }

    if (isEmbedMode()) {
      const embedToken = getEmbedAccessToken();
      if (!embedToken) {
        return Promise.reject(new Error('No embed authentication token available'));
      }
      if (isTokenExpired(embedToken)) {
        clearEmbedAccessToken();
        return Promise.reject(new Error('Embed session expired'));
      }
      config.headers.Authorization = `Bearer ${embedToken}`;
      return config;
    }

    if ((sessionEnding || endingAuthSession) && !isLogoutRequest(config)) {
      return Promise.reject(new Error('Session ending'));
    }

    const token = localStorage.getItem('access_token');
    console.log('Current token:', token ? 'exists' : 'not found');
    
    if (!token) {
      const onAuthScreen =
        window.location.pathname === '/login' ||
        window.location.pathname === '/callback';
      if (!onAuthScreen && !sessionEnding && !endingAuthSession) {
        logout();
      }
      return Promise.reject(new Error('No authentication token available'));
    }

    if (isTokenExpired(token)) {
      console.log('Token is expired, attempting to refresh');
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        console.log('No refresh token found, redirecting to login');
        logout();
        return Promise.reject(new Error('No refresh token available'));
      }

      try {
        const newToken = await refreshAccessToken(refreshToken);
        config.headers.Authorization = `Bearer ${newToken}`;
        console.log('Token refreshed successfully');
      } catch (error) {
        console.error('Failed to refresh token:', error);
        logout();
        return Promise.reject(error);
      }
    } else {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Using existing token');
    }

    console.log('Final request headers:', config.headers);
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log('Response received:', response.status);
    return response;
  },
  async (error) => {
    console.error('Response error:', error.response?.status, error.response?.data);
    
    const originalRequest = error.config;
    if (sessionEnding || endingAuthSession || isLogoutRequest(originalRequest)) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isEmbedMode()) {
        clearEmbedAccessToken();
        return Promise.reject(error);
      }

      console.log('Attempting to recover from 401 error');
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const newToken = await refreshAccessToken(refreshToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        console.log('Token refreshed, retrying request');
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Failed to recover from 401:', refreshError);
        logout();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

const refreshAccessToken = async (refreshToken) => {
  if (refreshAccessTokenInFlight) {
    return refreshAccessTokenInFlight;
  }

  refreshAccessTokenInFlight = (async () => {
    console.log('Refreshing access token');
    const params = new URLSearchParams();
    params.append('grant_type', 'refresh_token');
    params.append('refresh_token', refreshToken);
    params.append('client_id', CLIENT_ID);

    try {
      const response = await axios.post(`${AUTH_URL}/token`, params.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Basic ${btoa(`${CLIENT_ID}:${CLIENT_SECRET}`)}`
        }
      });

      if (sessionEnding) {
        throw new Error('Session ending');
      }

      const { access_token, refresh_token } = response.data;
      if (sessionEnding) {
        throw new Error('Session ending');
      }
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      if (sessionEnding) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        throw new Error('Session ending');
      }
      try {
        const p = JSON.parse(atob(access_token.split('.')[1]));
        if (typeof p.exp === 'number') {
          console.info(
            '[SupportHub auth] Access token renovado · novo exp (UTC):',
            new Date(p.exp * 1000).toISOString()
          );
        } else {
          console.info('[SupportHub auth] Access token renovado.');
        }
      } catch {
        console.info('[SupportHub auth] Access token renovado.');
      }
      scheduleProactiveAccessTokenRefresh();
      notifyAuthTokensUpdated();
      return access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    } finally {
      refreshAccessTokenInFlight = null;
    }
  })();

  return refreshAccessTokenInFlight;
};

export const exchangeCodeForToken = async (code) => {
  beginLogin();
  console.log('Exchanging code for token');
  const params = new URLSearchParams();
  params.append('grant_type', 'authorization_code');
  params.append('code', code);
  params.append('redirect_uri', REDIRECT_URI);
  params.append('client_id', CLIENT_ID);

  try {
    const response = await axios.post(`${AUTH_URL}/token`, params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Basic ${btoa(`${CLIENT_ID}:${CLIENT_SECRET}`)}`
      }
    });

    console.log('Code exchange successful');
    clearSessionRevoked();
    const { access_token, refresh_token } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    scheduleProactiveAccessTokenRefresh();
    notifyAuthTokensUpdated();

    return access_token;
  } catch (error) {
    console.error('Code exchange failed:', error);
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    }
    throw error;
  }
};

/**
 * Sessão “válida” para rotas e contexto: access ainda no prazo **ou** access expirado
 * mas com refresh_token (o axios renova antes ou durante a próxima chamada).
 * Evita flash para /login no intervalo entre exp do JWT e o fim do refresh.
 */
export const beginLogin = () => {
  sessionEnding = false;
  endingAuthSession = false;
};

export const discardLeftoverSession = () => {
  if (!requiresFreshLogin()) return;
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('id_token');
};

export const isAuthenticated = () => {
  if (requiresFreshLogin()) return false;

  if (isEmbedMode()) {
    const embedToken = getEmbedAccessToken();
    return !!(embedToken && !isTokenExpired(embedToken));
  }

  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');

  if (accessToken && !isTokenExpired(accessToken)) {
    return true;
  }
  if (refreshToken && accessToken && isTokenExpired(accessToken)) {
    return true;
  }
  return false;
};

export const getAuthorizationUrl = () => {
  const params = new URLSearchParams();
  params.append('response_type', 'code');
  params.append('client_id', CLIENT_ID);
  params.append('redirect_uri', REDIRECT_URI);
  params.append('scope', 'SupportHub:User SupportHub:Admin');
  params.append('state', Math.random().toString(36).substring(7));
  appendForceLogin(params);

  const url = `${AUTH_URL}/authorize?${params.toString()}`;
  return url;
};

export const logout = () => {
  clearStoredTokens();
  notifyAuthTokensUpdated();
  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
};

/** Encerra a sessão local e a sessão do servidor de autenticação. */
export const logoutAndEndAuthSession = () => {
  endingAuthSession = true;
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');
  clearStoredTokens();
  notifyAuthTokensUpdated();

  const apiBase = String(API_URL || '').replace(/\/$/, '');
  if (accessToken && apiBase) {
    fetch(`${apiBase}/api/private/user/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      keepalive: true,
    }).catch(() => {});
  }
  if (refreshToken) {
    revokeRefreshToken(refreshToken).catch(() => {});
  }

  endIdpSessionThen(`${window.location.origin}/login`);
};

if (typeof window !== 'undefined') {
  const at = localStorage.getItem('access_token');
  const rt = localStorage.getItem('refresh_token');
  if (at && rt) scheduleProactiveAccessTokenRefresh();
}

export { api };
export default api; 